import { HelpCircle, UserCheck, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export enum NotificationType {
  CUSTOMER = "CUSTOMER",
  USER = "USER",
}

interface NotificationTypeSelectorProps {
  selectedType: NotificationType;
  onTypeChange: (type: NotificationType) => void;
  children: (type: NotificationType) => React.ReactNode;
  className?: string;
}

export function NotificationTypeSelector({
  selectedType,
  onTypeChange,
  children,
  className,
}: NotificationTypeSelectorProps) {
  const { t } = useTranslation("settings");
  const [customerHelpOpen, setCustomerHelpOpen] = useState(false);
  const [userHelpOpen, setUserHelpOpen] = useState(false);

  return (
    <Tabs
      value={selectedType}
      onValueChange={(value) => onTypeChange(value as NotificationType)}
      className={className}
    >
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value={NotificationType.CUSTOMER} className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          {t("notifications.types.customer.title", { defaultValue: "Customer Notifications" })}
        </TabsTrigger>
        <TabsTrigger value={NotificationType.USER} className="flex items-center gap-2">
          <UserCheck className="h-4 w-4" />
          {t("notifications.types.user.title", { defaultValue: "Staff Notifications" })}
        </TabsTrigger>
      </TabsList>

      <TabsContent value={NotificationType.CUSTOMER} className="mt-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">
              {t("notifications.types.customer.title", { defaultValue: "Customer Notifications" })}
            </h3>
            <TooltipProvider>
              <Tooltip>
                <Popover open={customerHelpOpen} onOpenChange={setCustomerHelpOpen}>
                  <PopoverTrigger asChild>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => setCustomerHelpOpen(!customerHelpOpen)}
                      >
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="start" side="bottom" sideOffset={4}>
                    <div className="text-sm text-muted-foreground leading-relaxed">
                      {t("notifications.types.customer.description", {
                        defaultValue:
                          "Configure how customers receive appointment confirmations, reminders, and updates.",
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">{t("common.moreInfo", { defaultValue: "More info" })}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {children(NotificationType.CUSTOMER)}
        </div>
      </TabsContent>

      <TabsContent value={NotificationType.USER} className="mt-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">
              {t("notifications.types.user.title", { defaultValue: "Staff Notifications" })}
            </h3>
            <TooltipProvider>
              <Tooltip>
                <Popover open={userHelpOpen} onOpenChange={setUserHelpOpen}>
                  <PopoverTrigger asChild>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => setUserHelpOpen(!userHelpOpen)}
                      >
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="start" side="bottom" sideOffset={4}>
                    <div className="text-sm text-muted-foreground leading-relaxed">
                      {t("notifications.types.user.description", {
                        defaultValue:
                          "Configure how salon staff receive notifications about new appointments, cancellations, and daily summaries.",
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">{t("common.moreInfo", { defaultValue: "More info" })}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {children(NotificationType.USER)}
        </div>
      </TabsContent>
    </Tabs>
  );
}
